import React, { useEffect } from "react";
import { NavLink } from "react-router-dom";
import { useTheme } from "../theme/useTheme";

const STORAGE_KEY = "sidebar-collapsed";

interface NavigationLink {
  to: string;
  label: string;
  icon: string;
  description: string;
  comingSoon?: boolean;
}

const links: NavigationLink[] = [
  {
    to: "/dashboard",
    label: "Workspace",
    icon: "🏠",
    description: "Main overview",
  },
  {
    to: "/research/gene-disease-link",
    label: "Gene-Disease Link",
    icon: "🔗",
    description: "Mechanistic relationship explorer",
  },
  {
    to: "/gene-hub",
    label: "Gene Hub",
    icon: "🧬",
    description: "Curate & explore gene sets",
  },
  {
    to: "/disease-hub",
    label: "Disease Hub",
    icon: "🩺",
    description: "Stratify disease signals",
    comingSoon: true,
  },
];

export const Sidebar: React.FC = () => {
  const { mode, toggle, collapsed, setCollapsed } = useTheme();
  // const [collapsed, setCollapsed] = useState(false);

  useEffect(() => {
    const saved = localStorage.getItem(STORAGE_KEY);
    if (saved === "true") setCollapsed(true);
  }, []);

  useEffect(() => {
    localStorage.setItem(STORAGE_KEY, String(collapsed));
  }, [collapsed]);

  return (
    <nav
      className={`relative flex flex-col transition-all duration-300 ${
        collapsed ? "w-20" : "w-80"
      } ${
        mode === "light"
          ? "bg-gradient-to-br from-slate-50 via-blue-50 to-purple-100 shadow-2xl border-r border-white/20"
          : "bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 shadow-2xl border-r border-slate-700/50"
      } backdrop-blur-sm`}
      aria-label="Main"
    >
      {/* Header / Logo + Collapse Toggle */}
      <div
        className={`p-6 border-b ${
          mode === "light" ? "border-white/20" : "border-slate-700/50"
        }`}
      >
        <div className="flex items-center space-x-3 mb-2">
          {/* <div
            className="w-10 h-10 bg-gradient-to-br from-blue-500 to-purple-600 rounded-xl flex items-center justify-center shadow-lg flex-shrink-0 cursor-pointer hover:opacity-90 transition-opacity"
            onClick={() => setCollapsed(!collapsed)}
          >
            <span className="text-white font-bold text-lg">☰</span>
          </div> */}
          <img
            src="/targetx-logo.png"
            alt="TargetX Logo"
            className="w-10 h-10 object-contain"
            title="TargetX"
          />
          {!collapsed && (
            <div className="flex-1 min-w-0">
              <h1
                className={`text-xl font-bold bg-gradient-to-r from-blue-600 to-purple-600 bg-clip-text text-transparent`}
              >
                TargetX
              </h1>
              <p
                className={`text-xs ${
                  mode === "light" ? "text-gray-600" : "text-slate-400"
                }`}
              >
                Drug Discovery Platform
              </p>
            </div>
          )}
          <button
            onClick={() => setCollapsed(!collapsed)}
            aria-label={collapsed ? "Expand sidebar" : "Collapse sidebar"}
            className={`w-7 h-7 rounded-lg text-xs flex items-center justify-center transition-colors shadow-sm ${
              mode === "light"
                ? "bg-white/60 hover:bg-white/80 text-gray-600 border border-white/30"
                : "bg-slate-700 hover:bg-slate-600 text-slate-300 border border-slate-600"
            }`}
          >
            {collapsed ? "»" : "«"}
          </button>
        </div>
      </div>

      {/* Navigation */}
      <div
        className={`flex-1 px-6 py-6 overflow-y-auto ${
          mode === "light" ? "custom-scrollbar" : "darkcustom-scrollbar"
        }`}
      >
        <ul className="space-y-3">
          {links.map((link) => (
            <li key={link.to}>
              {link.comingSoon ? (
                <div
                  className={`group flex items-center ${
                    collapsed
                      ? "justify-center px-2 py-3"
                      : "space-x-4 px-5 py-4"
                  } rounded-2xl text-sm font-medium transition-all duration-300 opacity-70 cursor-not-allowed ${
                    mode === "light"
                      ? "text-gray-500 bg-white/20"
                      : "text-slate-500 bg-slate-800/20"
                  }`}
                  title={
                    collapsed
                      ? `${link.label} - ${link.description} (Coming Soon)`
                      : undefined
                  }
                >
                  <div
                    className={`p-2 rounded-lg transition-colors duration-200 ${
                      mode === "light" ? "bg-gray-100/50" : "bg-slate-700/30"
                    }`}
                  >
                    <span className="text-lg" aria-hidden>
                      {link.icon}
                    </span>
                  </div>
                  {!collapsed && (
                    <div className="flex-1">
                      <div className="font-semibold flex items-center space-x-2">
                        <span>{link.label}</span>
                        <span
                          className={`text-xs px-2 py-0.5 rounded-full ${
                            mode === "light"
                              ? "bg-gray-200 text-gray-600"
                              : "bg-slate-700 text-slate-400"
                          }`}
                        >
                          Soon
                        </span>
                      </div>
                      <div
                        className={`text-xs transition-colors duration-200 ${
                          mode === "light" ? "text-gray-400" : "text-slate-500"
                        }`}
                      >
                        {link.description}
                      </div>
                    </div>
                  )}
                </div>
              ) : (
                <NavLink
                  to={link.to}
                  className={({ isActive }) => {
                    const baseClasses = `group flex items-center ${
                      collapsed ? "justify-center px-2" : "space-x-4 px-5 py-4"
                    } rounded-2xl text-sm font-medium transition-all duration-300`;

                    if (isActive) {
                      return `${baseClasses} bg-gradient-to-r from-blue-500 to-purple-600 text-white shadow-xl transform scale-[1.02] border border-white/20`;
                    }

                    return `${baseClasses} ${
                      mode === "light"
                        ? "text-gray-700 hover:bg-white/60 hover:text-blue-600 hover:shadow-lg hover:transform hover:scale-[1.01] bg-white/30"
                        : "text-slate-300 hover:bg-slate-700/60 hover:text-blue-400 hover:shadow-lg hover:transform hover:scale-[1.01] bg-slate-800/30"
                    }`;
                  }}
                  title={
                    collapsed
                      ? `${link.label} - ${link.description}`
                      : undefined
                  }
                >
                  {({ isActive }) => (
                    <>
                      <div
                        className={`p-2 rounded-lg transition-colors duration-200 ${
                          isActive
                            ? "bg-white/20"
                            : mode === "light"
                              ? "group-hover:bg-blue-100"
                              : "group-hover:bg-slate-600"
                        }`}
                      >
                        <span className="text-lg" aria-hidden>
                          {link.icon}
                        </span>
                      </div>
                      {!collapsed && (
                        <div className="flex-1">
                          <div className="font-semibold">{link.label}</div>
                          <div
                            className={`text-xs transition-colors duration-200 ${
                              isActive
                                ? "text-blue-100"
                                : mode === "light"
                                  ? "text-gray-500 group-hover:text-blue-500"
                                  : "text-slate-400 group-hover:text-blue-400"
                            }`}
                          >
                            {link.description}
                          </div>
                        </div>
                      )}
                    </>
                  )}
                </NavLink>
              )}
            </li>
          ))}
        </ul>
      </div>

      {/* Theme Toggle */}
      <div
        className={`p-4 border-t ${
          mode === "light" ? "border-white/20" : "border-slate-700/50"
        }`}
      >
        <button
          onClick={toggle}
          className={`w-full flex items-center ${
            collapsed ? "justify-center" : "justify-center space-x-2"
          } px-3 py-3 rounded-xl text-sm font-medium transition-all duration-300 transform hover:scale-[1.02] shadow-sm ${
            mode === "light"
              ? "bg-white/60 hover:bg-white/80 text-gray-700 hover:text-blue-600 border border-white/30"
              : "bg-slate-700/60 hover:bg-slate-600/80 text-slate-300 hover:text-blue-400 border border-slate-600/50"
          }`}
          aria-label="Toggle theme"
          title={`Switch to ${mode === "light" ? "dark" : "light"} mode`}
        >
          <span className="text-lg">{mode === "light" ? "🌙" : "☀️"}</span>
          {!collapsed && (
            <span className="font-medium">
              {mode === "light" ? "Dark" : "Light"}
            </span>
          )}
        </button>
      </div>
    </nav>
  );
};
